import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useSearchParams } from "react-router-dom";
import MapContainer from "../components/map/MapContainer";
import DepthRangeFilter from "../components/map/DepthRangeFilter";
import FishMultiFilter from "../components/map/FishMultiFilter";
import MapEntityPopup from "../components/map/MapEntityPopup";
import { useAuth } from "../auth/AuthContext";
import { useNotification } from "../notifications/NotificationContext";
import { useDepthPointsLoader } from "../hooks/useDepthPointsLoader";
import { useMapWrapAnchor } from "../hooks/useMapWrapAnchor";
import { readSpotPopupTabsFromSearchParams } from "../utils/spotPopupUrl";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

function buildImageUrl(path) {
    if (!path) return "";
    if (typeof path !== "string") return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${API_BASE_URL}${path}`;
}

function getSpotPreviewPhoto(spot) {
    if (!spot) return "";
    return (
        spot.placePhotos?.[0]
        || spot.photos?.[0]
        || spot.mainPhoto
        || spot.photo
        || spot.image
        || spot.place?.image
        || ""
    );
}

function parseFishIdsParam(value) {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
    }
    return String(value)
        .split(",")
        .map((part) => Number(part.trim()))
        .filter((id) => Number.isFinite(id) && id > 0);
}

function createFilters(values) {
    return {
        q: values.q || "",
        fishIds: parseFishIdsParam(values.fishIds),
        onlyFromShore: values.onlyFromShore === "1" || values.onlyFromShore === true,
        hasParking: values.hasParking === "1" || values.hasParking === true,
        tentAllowed: values.tentAllowed === "1" || values.tentAllowed === true,
        nightFishingAllowed: values.nightFishingAllowed === "1" || values.nightFishingAllowed === true,
        suitableForChildren: values.suitableForChildren === "1" || values.suitableForChildren === true,
        minDepth: values.minDepth || "",
        maxDepth: values.maxDepth || "",
        favoritesOnly: values.favoritesOnly === "1" || values.favoritesOnly === true,
        likedOnly: values.likedOnly === "1" || values.likedOnly === true,
        minCapacity: values.minCapacity || "",
    };
}

const FILTER_PARAM_KEYS = [
    "q",
    "fishIds",
    "onlyFromShore",
    "hasParking",
    "tentAllowed",
    "nightFishingAllowed",
    "suitableForChildren",
    "minDepth",
    "maxDepth",
    "favoritesOnly",
    "likedOnly",
    "minCapacity",
];

function readFiltersFromSearchParams(searchParams) {
    return createFilters({
        q: searchParams.get("q") || "",
        fishIds: searchParams.get("fishIds") || "",
        onlyFromShore: searchParams.get("onlyFromShore") || "",
        hasParking: searchParams.get("hasParking") || "",
        tentAllowed: searchParams.get("tentAllowed") || "",
        nightFishingAllowed: searchParams.get("nightFishingAllowed") || "",
        suitableForChildren: searchParams.get("suitableForChildren") || "",
        minDepth: searchParams.get("minDepth") || "",
        maxDepth: searchParams.get("maxDepth") || "",
        favoritesOnly: searchParams.get("favoritesOnly") || "",
        likedOnly: searchParams.get("likedOnly") || "",
        minCapacity: searchParams.get("minCapacity") || "",
    });
}

function getFiltersSearchKey(searchParams) {
    return FILTER_PARAM_KEYS.map((key) => searchParams.get(key) ?? "").join("\0");
}

function readFiltersFromSearchKey(filtersSearchKey) {
    const values = filtersSearchKey.split("\0");
    const entries = {};
    FILTER_PARAM_KEYS.forEach((key, index) => {
        entries[key] = values[index] || "";
    });
    return createFilters(entries);
}

function writeFiltersToSearchParams(baseParams, filters) {
    const source = typeof window !== "undefined" ? window.location.search : baseParams.toString();
    const nextParams = new URLSearchParams(source);
    FILTER_PARAM_KEYS.forEach((key) => nextParams.delete(key));
    if (filters.q) nextParams.set("q", filters.q);
    if (filters.fishIds?.length) nextParams.set("fishIds", filters.fishIds.join(","));
    if (filters.onlyFromShore) nextParams.set("onlyFromShore", "1");
    if (filters.hasParking) nextParams.set("hasParking", "1");
    if (filters.tentAllowed) nextParams.set("tentAllowed", "1");
    if (filters.nightFishingAllowed) nextParams.set("nightFishingAllowed", "1");
    if (filters.suitableForChildren) nextParams.set("suitableForChildren", "1");
    if (filters.minDepth !== "") nextParams.set("minDepth", String(filters.minDepth));
    if (filters.maxDepth !== "") nextParams.set("maxDepth", String(filters.maxDepth));
    if (filters.favoritesOnly) nextParams.set("favoritesOnly", "1");
    if (filters.likedOnly) nextParams.set("likedOnly", "1");
    if (filters.minCapacity !== "") nextParams.set("minCapacity", String(filters.minCapacity));
    return nextParams;
}

const EMPTY_FILTERS = createFilters({});

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

export default function MapPage() {
    const { showNotification } = useNotification();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, ready: authReady } = useAuth();
    const [spots, setSpots] = useState([]);
    const { depthPoints, loadDepthPoints } = useDepthPointsLoader(authReady && Boolean(user));
    const [fishList, setFishList] = useState([]);
    const [activeEntity, setActiveEntity] = useState(null);
    const [previewSpot, setPreviewSpot] = useState(null);
    const [markerSyncKey, setMarkerSyncKey] = useState(0);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const modalSpotId = searchParams.get("spot-id");
    const [message, setMessage] = useState("");
    const [selectedCoords, setSelectedCoords] = useState(null);
    const mapWrapRef = useRef(null);
    const previewPopupLayout = useMapWrapAnchor(mapWrapRef, Boolean(previewSpot));
    const [previewLikePending, setPreviewLikePending] = useState(false);
    const [previewFavoritePending, setPreviewFavoritePending] = useState(false);
    const previewLikePendingRef = useRef(false);
    const previewFavoritePendingRef = useRef(false);
    const filtersSearchKey = useMemo(() => getFiltersSearchKey(searchParams), [searchParams]);
    const appliedFilters = useMemo(
        () => readFiltersFromSearchKey(filtersSearchKey),
        [filtersSearchKey],
    );
    const [draftFilters, setDraftFilters] = useState(() => readFiltersFromSearchParams(searchParams));

    useEffect(() => {
        setDraftFilters(appliedFilters);
    }, [appliedFilters]);

    useEffect(() => {
        if (!filtersOpen) {
            return undefined;
        }

        document.documentElement.classList.add("map-filters-open");
        document.body.classList.add("map-filters-open");
        return () => {
            document.documentElement.classList.remove("map-filters-open");
            document.body.classList.remove("map-filters-open");
        };
    }, [filtersOpen]);

    useEffect(() => {
        if (!filtersOpen) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key !== "Escape" || activeEntity) {
                return;
            }
            setFiltersOpen(false);
        };

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [filtersOpen, activeEntity]);

    const loadSpots = useCallback((filtersToApply) => {
        const params = {};

        if (filtersToApply) {
            if (filtersToApply.q) params.q = filtersToApply.q;
            if (filtersToApply.fishIds?.length) {
                params.fish_ids = filtersToApply.fishIds;
            }
            if (filtersToApply.onlyFromShore) params.fishing_from_shore = true;
            if (filtersToApply.hasParking) params.has_parking = true;
            if (filtersToApply.tentAllowed) params.tent_allowed = true;
            if (filtersToApply.nightFishingAllowed) params.night_fishing_allowed = true;
            if (filtersToApply.suitableForChildren) params.suitable_for_children = true;
            if (filtersToApply.minDepth !== "") params.min_depth = Number(filtersToApply.minDepth);
            if (filtersToApply.maxDepth !== "") params.max_depth = Number(filtersToApply.maxDepth);
            if (filtersToApply.minCapacity !== "") params.min_capacity = Number(filtersToApply.minCapacity);
            if (filtersToApply.favoritesOnly && user) params.favorites_only = true;
            if (filtersToApply.likedOnly && user) params.liked_only = true;
        }

        axios.get(`${API_BASE_URL}/api/spots`, { params })
            .then((spotsResponse) => {
                setSpots(spotsResponse.data || []);
            })
            .catch(() => setMessage("Не удалось загрузить точки. Проверьте соединение с сервером."));
    }, [user]);

    useEffect(() => {
        loadSpots(appliedFilters);
    }, [loadSpots, appliedFilters]);
    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/fish`).then((res) => setFishList(res.data || [])).catch(() => {});
    }, []);
    const updateDraftFilter = useCallback((key, value) => {
        setDraftFilters((prev) => {
            const next = { ...prev };
            if (typeof prev[key] === "boolean") {
                next[key] = Boolean(value);
                return next;
            }
            next[key] = value === "" || value == null ? "" : String(value);
            return next;
        });
    }, []);

    const updateDraftDepthRange = useCallback((min, max) => {
        setDraftFilters((prev) => ({ ...prev, minDepth: min, maxDepth: max }));
    }, []);

    const updateDraftFishIds = useCallback((fishIds) => {
        setDraftFilters((prev) => ({ ...prev, fishIds }));
    }, []);

    const applyFilters = useCallback(() => {
        setSearchParams(writeFiltersToSearchParams(searchParams, draftFilters), { replace: true });
        filtersOpen && setFiltersOpen(false);
    }, [draftFilters, searchParams, setSearchParams, filtersOpen]);

    const resetFilters = useCallback(() => {
        filtersOpen && setFiltersOpen(false);
        setDraftFilters(EMPTY_FILTERS);
        setSearchParams(writeFiltersToSearchParams(searchParams, EMPTY_FILTERS), { replace: true });
    }, [searchParams, setSearchParams, filtersOpen]);

    const openFilters = useCallback(() => {
        setPreviewSpot(null);
        setFiltersOpen(true);
    }, []);

    const handleSpotClick = useCallback((spot) => {
        setFiltersOpen(false);
        if (modalSpotId) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("spot-id");
            nextParams.delete("tab");
            nextParams.delete("photo_tab");
            nextParams.delete("weather_subtab");
            setSearchParams(nextParams, { replace: true });
        }
        setPreviewSpot(spot);
        setMarkerSyncKey((value) => value + 1);
        axios.get(`${API_BASE_URL}/api/spots/${spot.id}`, { withCredentials: true })
            .then((response) => {
                const detailedSpot = response?.data;
                if (!detailedSpot?.id) return;
                setPreviewSpot((current) => {
                    if (!current || Number(current.id) !== Number(detailedSpot.id)) return current;
                    return { ...current, ...detailedSpot };
                });
            })
            .catch(() => {});
    }, [modalSpotId, searchParams, setSearchParams]);

    const togglePreviewLike = useCallback(async () => {
        if (!user || !previewSpot?.id || previewLikePendingRef.current) return;
        previewLikePendingRef.current = true;
        setPreviewLikePending(true);
        const spotId = previewSpot.id;
        const wasLiked = Boolean(previewSpot.userLiked);
        try {
            if (wasLiked) {
                await axios.delete(`${API_BASE_URL}/api/likes`, { data: { entityType: "spot", entityId: Number(spotId) }, withCredentials: true });
                setPreviewSpot((current) => {
                    if (!current || Number(current.id) !== Number(spotId)) return current;
                    return {
                        ...current,
                        userLiked: false,
                        likesCount: Math.max(0, Number(current.likesCount ?? 0) - 1),
                    };
                });
            } else {
                await axios.post(`${API_BASE_URL}/api/likes`, { entityType: "spot", entityId: Number(spotId) }, { withCredentials: true });
                setPreviewSpot((current) => {
                    if (!current || Number(current.id) !== Number(spotId)) return current;
                    return {
                        ...current,
                        userLiked: true,
                        likesCount: Number(current.likesCount ?? 0) + 1,
                    };
                });
            }
            loadSpots();
        } catch (e) {
            console.log(e);
            showNotification({ type: "error", text: e?.response?.data?.error || "Не удалось изменить лайк." });
        } finally {
            previewLikePendingRef.current = false;
            setPreviewLikePending(false);
        }
    }, [user, previewSpot, loadSpots, showNotification]);

    const togglePreviewFavorite = useCallback(async () => {
        if (!user || !previewSpot?.id || previewFavoritePendingRef.current) return;
        previewFavoritePendingRef.current = true;
        setPreviewFavoritePending(true);
        const spotId = previewSpot.id;
        const wasFav = Boolean(previewSpot.userFavorited);
        try {
            if (wasFav) {
                await axios.delete(`${API_BASE_URL}/api/favorites/spots/${spotId}`, { withCredentials: true });
                setPreviewSpot((current) => {
                    if (!current || Number(current.id) !== Number(spotId)) return current;
                    return {
                        ...current,
                        userFavorited: false,
                        favoritesCount: Math.max(0, Number(current.favoritesCount ?? 0) - 1),
                    };
                });
            } else {
                await axios.post(`${API_BASE_URL}/api/favorites/spots/${spotId}`, {}, { withCredentials: true });
                setPreviewSpot((current) => {
                    if (!current || Number(current.id) !== Number(spotId)) return current;
                    return {
                        ...current,
                        userFavorited: true,
                        favoritesCount: Number(current.favoritesCount ?? 0) + 1,
                    };
                });
            }
            loadSpots();
        } catch (e) {
            showNotification({ type: "error", text: e?.response?.data?.error || "Не удалось изменить избранное." });
        } finally {
            previewFavoritePendingRef.current = false;
            setPreviewFavoritePending(false);
        }
    }, [user, previewSpot, loadSpots, showNotification]);

    const openEntityPopup = useCallback((spot) => {
        if (!spot) return;
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("spot-id", String(spot.id));
        nextParams.delete("tab");
        nextParams.delete("photo_tab");
        nextParams.delete("weather_subtab");
        setSearchParams(nextParams, { replace: true });
        if (Number.isFinite(Number(spot.latitude)) && Number.isFinite(Number(spot.longitude))) {
            setSelectedCoords([Number(spot.latitude), Number(spot.longitude)]);
        }
        setPreviewSpot(null);
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!modalSpotId) {
            setActiveEntity(null);
            return;
        }
        const spotIdNum = Number(modalSpotId);
        if (!Number.isFinite(spotIdNum)) {
            setActiveEntity(null);
            return;
        }
        const match = spots.find((spot) => Number(spot.id) === spotIdNum);
        if (match) {
            if (activeEntity?.id === match.id) {
                return;
            }
            setActiveEntity({
                id: match.id,
                kind: "spot",
                title: match.place?.name || `Точка #${match.id}`,
                payload: match,
                ...readSpotPopupTabsFromSearchParams(searchParams),
            });
            if (Number.isFinite(Number(match.latitude)) && Number.isFinite(Number(match.longitude))) {
                setSelectedCoords([Number(match.latitude), Number(match.longitude)]);
            }
            return;
        }
        let cancelled = false;
        axios.get(`${API_BASE_URL}/api/spots/${spotIdNum}`, { withCredentials: true })
            .then((response) => {
                if (cancelled) return;
                const spot = response?.data;
                if (!spot?.id) {
                    setActiveEntity(null);
                    return;
                }
                if (activeEntity?.id === spot.id) {
                    return;
                }
                setActiveEntity({
                    id: spot.id,
                    kind: "spot",
                    title: spot.place?.name || `Точка #${spot.id}`,
                    payload: spot,
                    ...readSpotPopupTabsFromSearchParams(searchParams),
                });
                if (Number.isFinite(Number(spot.latitude)) && Number.isFinite(Number(spot.longitude))) {
                    setSelectedCoords([Number(spot.latitude), Number(spot.longitude)]);
                }
            })
            .catch(() => {
                if (!cancelled) setActiveEntity(null);
            });
        return () => {
            cancelled = true;
        };
    }, [modalSpotId, spots, activeEntity?.id, searchParams]);

    const closeSpotModal = useCallback(() => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("spot-id");
        nextParams.delete("tab");
        nextParams.delete("photo_tab");
        nextParams.delete("weather_subtab");
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams]);

    return (
        <section className="map-page-template map-page-template--glass">
            <section className="map-card map-card--full">
                <div className="map-page-layout">
                    {filtersOpen && (
                        <div
                            className="map-filters-overlay"
                            onClick={() => setFiltersOpen(false)}
                            aria-hidden="true"
                        />
                    )}

                    <aside className={`map-filters-panel ${previewSpot ? "is-disabled" : ""} ${filtersOpen ? "is-open" : ""}`}>
                        <div className="map-filters map-filters--sidebar">
                            <div className="map-filters__head">
                                <span className="map-filters__title">Фильтры</span>
                                {filtersOpen && (
                                    <button
                                        type="button"
                                        className="map-filters__close-in-head"
                                        onClick={() => setFiltersOpen(false)}
                                        aria-label="Закрыть фильтры"
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                        </svg>
                                    </button>
                                )}
                            </div>
                            <div className={`map-filters__body ${filtersOpen ? "is-open" : ""}`}>
                                <FishMultiFilter
                                    label="Рыба"
                                    fishList={fishList}
                                    selectedIds={draftFilters.fishIds}
                                    onChange={updateDraftFishIds}
                                />
                                <hr className="map-filters__divider" />
                                <div className="filter-field map-filter-number">
                                    <div className="fish-multi-filter__head">
                                        <label className="fish-multi-filter__label" htmlFor="map-filter-capacity">
                                            Вместимость
                                        </label>
                                    </div>
                                    <input
                                        id="map-filter-capacity"
                                        className="ui-input map-filter-number__input"
                                        type="number"
                                        min="1"
                                        max="20"
                                        step="1"
                                        value={draftFilters.minCapacity}
                                        onChange={(e) => updateDraftFilter("minCapacity", e.target.value)}
                                        placeholder="человек"
                                    />
                                </div>
                                <DepthRangeFilter
                                    label="Глубина"
                                    minValue={draftFilters.minDepth}
                                    maxValue={draftFilters.maxDepth}
                                    onRangeChange={updateDraftDepthRange}
                                />
                                <div className="map-filters__checks">
                                    <div className="fish-multi-filter__head">
                                        <label className="fish-multi-filter__label" htmlFor="map-filter-capacity">
                                            Другое
                                        </label>
                                    </div>
                                    {user && (
                                        <label className="map-filters__check">
                                            <input type="checkbox" checked={draftFilters.favoritesOnly} onChange={(e) => updateDraftFilter("favoritesOnly", e.target.checked)} />
                                            <span>Избранные</span>
                                        </label>
                                    )}
                                    {user && (
                                        <label className="map-filters__check">
                                            <input type="checkbox" checked={draftFilters.likedOnly} onChange={(e) => updateDraftFilter("likedOnly", e.target.checked)} />
                                            <span>Понравившиеся</span>
                                        </label>
                                    )}
                                    <label className="map-filters__check">
                                        <input type="checkbox" checked={draftFilters.onlyFromShore} onChange={(e) => updateDraftFilter("onlyFromShore", e.target.checked)} />
                                        <span>Береговая точка</span>
                                    </label>
                                    <label className="map-filters__check">
                                        <input type="checkbox" checked={draftFilters.hasParking} onChange={(e) => updateDraftFilter("hasParking", e.target.checked)} />
                                        <span>Парковка доступна</span>
                                    </label>
                                    <label className="map-filters__check">
                                        <input type="checkbox" checked={draftFilters.tentAllowed} onChange={(e) => updateDraftFilter("tentAllowed", e.target.checked)} />
                                        <span>Можно с палаткой</span>
                                    </label>
                                    <label className="map-filters__check">
                                        <input type="checkbox" checked={draftFilters.nightFishingAllowed} onChange={(e) => updateDraftFilter("nightFishingAllowed", e.target.checked)} />
                                        <span>Ночная рыбалка</span>
                                    </label>
                                    <label className="map-filters__check">
                                        <input type="checkbox" checked={draftFilters.suitableForChildren} onChange={(e) => updateDraftFilter("suitableForChildren", e.target.checked)} />
                                        <span>Можно с детьми</span>
                                    </label>
                                </div>
                                <div className="map-filters__actions">
                                    <button type="button" className="toolbar-btn map-filters__apply" onClick={applyFilters}>
                                        Применить фильтры
                                    </button>
                                    <button type="button" className="btn-outline map-filters__reset" onClick={resetFilters}>
                                        Сбросить
                                    </button>
                                </div>
                            </div>
                        </div>
                    </aside>
                    <div className="map-page-layout__map">
                        <button
                            type="button"
                            className="map-filters-toggle-btn"
                            onClick={openFilters}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="4" y1="6" x2="20" y2="6"></line>
                                <line x1="4" y1="12" x2="20" y2="12"></line>
                                <line x1="4" y1="18" x2="20" y2="18"></line>
                                <circle cx="7" cy="6" r="2" fill="currentColor"></circle>
                                <circle cx="17" cy="12" r="2" fill="currentColor"></circle>
                                <circle cx="11" cy="18" r="2" fill="currentColor"></circle>
                            </svg>
                            Фильтры
                        </button>
            <div className="map-wrap" ref={mapWrapRef}>
                <MapContainer
                    spots={spots}
                    depthPoints={depthPoints}
                    onSpotClick={handleSpotClick}
                    onViewportChange={loadDepthPoints}
                    onMapClick={() => setPreviewSpot(null)}
                    selectedCoords={selectedCoords}
                    selectedSpotId={previewSpot?.id ?? activeEntity?.id ?? null}
                    markerSyncKey={markerSyncKey}
                />
                {previewSpot && (
                    <div
                        className={`spot-mini-popup spot-mini-popup--${previewPopupLayout.anchor}`}
                        style={previewPopupLayout.anchor === "viewport" ? { "--spot-popup-anchor-x": `${previewPopupLayout.centerX}px` } : undefined}
                    >
                        <div className="spot-mini-popup__panel">
                        <div className="spot-mini-popup__head">
                            <span className="popup-title">{previewSpot.place?.name || previewSpot.name || `Точка #${previewSpot.id}`}</span>
                            <button type="button" className="icon-btn" onClick={() => setPreviewSpot(null)}>×</button>
                        </div>
                        <div className="spot-mini-popup__body">
                            <div className="spot-mini-popup__row">
                                <span className="spot-mini-popup__label">Место:</span>
                                <span>{previewSpot.place?.name || previewSpot.place?.type || "Водоем"}</span>
                            </div>
                            <div className="spot-mini-popup__row">
                                <span className="spot-mini-popup__label">Глубина:</span>
                                <span>{previewSpot.maxDepth ?? previewSpot.depthMax ?? "-"}м (макс.)</span>
                            </div>
                            {previewSpot.fishingFromBoat && (
                                <div className="spot-mini-popup__row">
                                    <span className="spot-mini-popup__label">Точка ловли:</span>
                                    <span>Лодка</span>
                                </div>
                            )}
                            {previewSpot.fishingFromShore && (
                                <div className="spot-mini-popup__row">
                                    <span className="spot-mini-popup__label">Точка ловли:</span>
                                    <span>Берег</span>
                                </div>
                            )}
                            <div className="spot-mini-popup__row">
                                <span className="spot-mini-popup__label">Рыба:</span>
                                <span>
                                    {Array.isArray(previewSpot.fish) && previewSpot.fish.length
                                        ? previewSpot.fish.slice(0, 5).map((f) => f.name || f).join(", ")
                                        : "Рыба не указана"}
                                </span>
                            </div>
                            {!!getSpotPreviewPhoto(previewSpot) && (
                                <img
                                    src={buildImageUrl(getSpotPreviewPhoto(previewSpot))}
                                    alt={previewSpot.place?.name || previewSpot.name || "Точка"}
                                    className="spot-mini-popup__image"
                                    width={320}
                                    height={120}
                                    decoding="async"
                                />
                            )}
                            <div className="spot-mini-popup__actions">
                                <div className="spot-mini-popup__metrics" aria-label="Лайки и избранное">
                                    {user ? (
                                        <>
                                            <button
                                                type="button"
                                                className={previewSpot.userLiked ? "btn-liked" : "btn-like"}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    togglePreviewLike();
                                                }}
                                                disabled={previewLikePending}
                                                aria-label={previewSpot.userLiked ? "Убрать лайк" : "Поставить лайк"}
                                            >
                                                <span
                                                    aria-hidden
                                                    style={{ opacity: previewSpot.userLiked ? 1 : 0.45 }}
                                                >
                                                    ♥
                                                </span>
                                                <span>{previewSpot.likesCount ?? 0}</span>
                                            </button>
                                            <button
                                                type="button"
                                                className={previewSpot.userFavorited ? "btn-liked" : "btn-like"}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    togglePreviewFavorite();
                                                }}
                                                disabled={previewFavoritePending}
                                                aria-label={previewSpot.userFavorited ? "Убрать из избранного" : "В избранное"}
                                            >
                                                <span aria-hidden>
                                                    {previewSpot.userFavorited ? "★" : "☆"}
                                                </span>
                                                <span>{previewSpot.favoritesCount ?? 0}</span>
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <span className="spot-mini-popup__metric" title="Лайки">
                                                <span aria-hidden style={{ opacity: 0.45 }}>♥</span>
                                                <span>{previewSpot.likesCount ?? 0}</span>
                                            </span>
                                            <span className="spot-mini-popup__metric" title="Избранное">
                                                <span aria-hidden>☆</span>
                                                <span>{previewSpot.favoritesCount ?? 0}</span>
                                            </span>
                                        </>
                                    )}
                                </div>
                                <button type="button" className="btn-details spot-mini-popup__details-btn" onClick={() => openEntityPopup(previewSpot)}>
                                    О точке →
                                </button>
                            </div>
                        </div>
                        </div>
                    </div>
                )}
            </div>
                    </div>
                </div>
            {message && <p className="map-page-message">{message}</p>}
            <MapEntityPopup
                entity={activeEntity}
                onClose={closeSpotModal}
                onChanged={loadSpots}
            />
            </section>
        </section>
    );
}

